import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { trackEvent } from '../lib/analytics'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL

/**
 * Public sample test page — no auth required.
 *
 * Loads a whitelisted sample test from the public API, lets the visitor
 * answer all questions in the browser, then submits all answers in one
 * request to /api/public/sample-tests/{id}/evaluate and shows the result
 * inline. No data is persisted to the user account.
 */
export default function SampleTest() {
  const { testId } = useParams()
  const navigate = useNavigate()
  const [stage, setStage] = useState('loading') // loading | error | taking | result
  const [test, setTest] = useState(null)
  const [questions, setQuestions] = useState([])
  const [answers, setAnswers] = useState({})
  const [currentIndex, setCurrentIndex] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState(null)
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        setStage('loading')
        const resp = await fetch(`${BACKEND_URL}/api/public/sample-tests/${testId}`)
        if (!resp.ok) {
          setErrorMsg(resp.status === 404 ? 'Tento ukážkový test nie je k dispozícii.' : 'Nepodarilo sa načítať ukážkový test.')
          setStage('error')
          return
        }
        const data = await resp.json()
        if (cancelled) return
        setTest({
          id: data.id,
          title: data.title,
          subject: data.subject,
          description: data.description,
          intro_text: data.intro_text || '',
          topics_text: data.topics_text || '',
        })
        setQuestions(data.questions || [])
        setStage('taking')
        trackEvent('sample_test_started', {
          test_id: data.id,
          subject: data.subject || undefined,
          question_count: (data.questions || []).length,
        })
      } catch (e) {
        if (!cancelled) {
          setErrorMsg('Nepodarilo sa načítať ukážkový test.')
          setStage('error')
        }
      }
    }
    load()
    return () => { cancelled = true }
  }, [testId])

  const current = questions[currentIndex]
  const answeredCount = useMemo(
    () => Object.values(answers).filter(v => (v ?? '').toString().trim() !== '').length,
    [answers]
  )

  function setAnswer(qid, value) {
    setAnswers(prev => ({ ...prev, [qid]: value }))
  }

  async function submit() {
    setSubmitting(true)
    try {
      const resp = await fetch(`${BACKEND_URL}/api/public/sample-tests/${testId}/evaluate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
      })
      if (!resp.ok) {
        setErrorMsg('Nepodarilo sa odoslať odpovede.')
        setStage('error')
        return
      }
      const data = await resp.json()
      setResult(data)
      setStage('result')
      trackEvent('sample_test_completed', {
        test_id: data.test_id,
        subject: data.subject || undefined,
        percentage: Math.round(data.percentage || 0),
      })
      trackEvent('sample_result_viewed', {
        test_id: data.test_id,
        subject: data.subject || undefined,
      })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } finally {
      setSubmitting(false)
    }
  }

  if (stage === 'loading') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-slate-600 text-sm" data-testid="sample-test-loading">Načítavam ukážkový test…</div>
      </div>
    )
  }

  if (stage === 'error') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-6 text-center" data-testid="sample-test-error">
          <h2 className="text-lg font-bold text-slate-900 mb-2">Nedá sa otvoriť</h2>
          <p className="text-sm text-slate-600 mb-4">{errorMsg}</p>
          <button
            onClick={() => navigate('/#ukazkove-testy')}
            className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
          >
            Späť na ukážky
          </button>
        </div>
      </div>
    )
  }

  if (stage === 'result' && result) {
    const pct = Math.round(result.percentage || 0)
    return (
      <div className="container mx-auto px-4 py-10 max-w-3xl" data-testid="sample-test-result">
        <div className="rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-blue-50 ring-1 ring-emerald-200/60 shadow-sm p-6 sm:p-8 mb-6">
          <div className="text-xs font-semibold tracking-wider text-emerald-700 mb-2">UKÁŽKOVÝ TEST – VÝSLEDOK</div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-1">{result.test_title}</h1>
          <p className="text-sm text-slate-600 mb-5">{result.subject}</p>
          <div className="flex flex-wrap items-end gap-4 mb-4">
            <div>
              <div className="text-5xl font-extrabold text-blue-700 leading-none">{pct} %</div>
              <div className="text-xs text-slate-500 mt-1">úspešnosť</div>
            </div>
            <div className="text-sm text-slate-700">
              <div><span className="font-semibold">{result.correct_count}</span> z <span className="font-semibold">{result.total_questions}</span> otázok správne</div>
              <div className="text-slate-500">Body: {result.score}/{result.max_score}</div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate('/register')}
              className="flex-1 px-5 py-3 bg-gradient-to-b from-orange-500 to-orange-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-orange-500/30 hover:from-orange-600 hover:to-orange-700"
              data-testid="sample-result-cta-register"
            >
              Chcem pokračovať v príprave
            </button>
            <button
              onClick={() => navigate('/login')}
              className="flex-1 px-5 py-3 bg-white text-blue-700 rounded-xl font-bold text-sm border-2 border-blue-600 hover:bg-blue-50"
              data-testid="sample-result-cta-login"
            >
              Vytvoriť účet / Prihlásiť sa
            </button>
          </div>
        </div>

        {/* Per-question review */}
        <div className="space-y-3" data-testid="sample-result-answers">
          {result.answers.map((a, idx) => (
            <div
              key={a.question_id}
              className={`rounded-2xl ring-1 shadow-sm p-4 sm:p-5 bg-white ${a.is_correct ? 'ring-emerald-200' : 'ring-rose-200'}`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="text-xs font-semibold text-slate-500">Otázka {idx + 1}</div>
                <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${a.is_correct ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                  {a.is_correct ? 'Správne' : 'Nesprávne'}
                </span>
              </div>
              <p className="text-sm sm:text-base text-slate-900 font-medium mb-2 whitespace-pre-line">{a.question_text}</p>
              {a.user_answer ? (
                <p className="text-sm text-slate-700"><span className="text-slate-500">Tvoja odpoveď:</span> <span className="font-semibold">{a.user_answer}</span></p>
              ) : (
                <p className="text-sm text-slate-500 italic">Bez odpovede</p>
              )}
              {!a.is_correct && (
                <p className="text-sm text-emerald-700 mt-1"><span className="text-slate-500">Správna odpoveď:</span> <span className="font-semibold">{a.correct_answer}</span></p>
              )}
              {a.explanation?.explanation && (
                <div className="mt-3 rounded-lg bg-blue-50 border border-blue-100 p-3 text-sm text-slate-700">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-blue-700 mb-1">Vysvetlenie</div>
                  {a.explanation.explanation}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-2xl bg-blue-50/70 ring-1 ring-blue-100 p-5 text-center">
          <p className="text-sm text-slate-700 mb-3">Páči sa Ti, ako EduMisia učí? Pokračuj s celým programom prípravy na Testovanie 9.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/register')}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700"
              data-testid="sample-result-footer-register"
            >
              Vytvoriť účet
            </button>
            <button
              onClick={() => navigate('/pricing')}
              className="px-5 py-2.5 bg-white text-blue-700 rounded-xl font-bold text-sm border-2 border-blue-600 hover:bg-blue-50"
              data-testid="sample-result-footer-pricing"
            >
              Pozrieť cenník
            </button>
          </div>
        </div>
      </div>
    )
  }

  // stage === 'taking'
  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl" data-testid="sample-test-taking">
      <div className="rounded-3xl bg-gradient-to-br from-slate-50 via-white to-blue-50/60 ring-1 ring-slate-200/70 shadow-sm p-5 sm:p-7 mb-5">
        <div className="text-xs font-semibold tracking-wider text-emerald-700 mb-1">UKÁŽKOVÝ TEST · BEZ REGISTRÁCIE</div>
        <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900">{test.title}</h1>
        {test.description && <p className="text-sm text-slate-600 mt-1">{test.description}</p>}
        <div className="mt-3 flex items-center justify-between text-xs text-slate-600">
          <span>Otázka {currentIndex + 1} z {questions.length}</span>
          <span>Odpovedané: {answeredCount}/{questions.length}</span>
        </div>
        <div className="mt-2 h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all"
            style={{ width: `${questions.length ? ((currentIndex + 1) / questions.length) * 100 : 0}%` }}
          />
        </div>
      </div>

      {/* Intro text (only shown on the first question so guests get the same
          context a logged-in user gets on the test intro screen). */}
      {currentIndex === 0 && test.intro_text && (
        <div
          className="mb-5 rounded-2xl bg-blue-50 border-l-4 border-blue-400 p-5 sm:p-6"
          data-testid="sample-intro-text"
        >
          <h3 className="font-bold text-slate-900 mb-2">Úvod</h3>
          <p className="text-sm sm:text-base text-slate-700 whitespace-pre-line leading-relaxed">
            {test.intro_text}
          </p>
        </div>
      )}
      {currentIndex === 0 && test.topics_text && (
        <div
          className="mb-5 rounded-2xl bg-green-50 border-l-4 border-green-500 p-5 sm:p-6"
          data-testid="sample-topics-text"
        >
          <h3 className="font-bold text-slate-900 mb-2">Témy</h3>
          <p className="text-sm sm:text-base text-slate-700 whitespace-pre-line leading-relaxed">
            {test.topics_text}
          </p>
        </div>
      )}

      {/* Passage (ukážka) — mirrors the authenticated test rendering so the
          question "Podľa ukážky N" makes sense to guests. */}
      {current && current.passage && (
        <div
          className="mb-5 rounded-2xl bg-[#f0f7ff] border-2 border-blue-600 p-4 sm:p-5"
          data-testid={`sample-passage-${currentIndex + 1}`}
        >
          <div className="text-sm font-semibold text-blue-800 mb-2">
            {current.passage.title}
          </div>
          <div
            className="text-[15px] leading-relaxed text-slate-800 whitespace-pre-wrap"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            {current.passage.content}
          </div>
        </div>
      )}

      {current && (
        <div className="rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm p-5 sm:p-6 mb-5" data-testid={`sample-question-${currentIndex + 1}`}>
          <p className="text-base sm:text-lg font-semibold text-slate-900 whitespace-pre-line mb-4">
            {current.question_text}
          </p>
          {current.image_url && (
            <img src={current.image_url} alt="" className="mb-4 max-h-72 rounded-lg ring-1 ring-slate-200" />
          )}

          {current.question_type === 'multiple_choice' ? (
            <div className="space-y-2">
              {(current.options || []).map(opt => {
                const checked = answers[current.id] === opt.key
                return (
                  <label
                    key={opt.key}
                    className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${checked ? 'bg-blue-50 border-blue-400 ring-2 ring-blue-100' : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'}`}
                  >
                    <input
                      type="radio"
                      name={`q-${current.id}`}
                      className="mt-1"
                      checked={checked}
                      onChange={() => setAnswer(current.id, opt.key)}
                      data-testid={`sample-option-${opt.key}`}
                    />
                    <span className="text-sm text-slate-800"><span className="font-bold mr-1">{opt.key}.</span>{opt.text}</span>
                  </label>
                )
              })}
            </div>
          ) : (
            <input
              type="text"
              className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
              placeholder="Tvoja odpoveď"
              value={answers[current.id] || ''}
              onChange={e => setAnswer(current.id, e.target.value)}
              data-testid="sample-short-answer-input"
            />
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-3" data-testid="sample-nav">
        <button
          type="button"
          onClick={() => setCurrentIndex(i => Math.max(0, i - 1))}
          disabled={currentIndex === 0}
          className="px-4 py-2.5 rounded-xl bg-white text-slate-700 border border-slate-300 font-semibold text-sm disabled:opacity-40 hover:bg-slate-50"
          data-testid="sample-prev-btn"
        >
          ← Späť
        </button>
        {currentIndex < questions.length - 1 ? (
          <button
            type="button"
            onClick={() => setCurrentIndex(i => Math.min(questions.length - 1, i + 1))}
            className="px-5 py-2.5 rounded-xl bg-blue-600 text-white font-bold text-sm hover:bg-blue-700"
            data-testid="sample-next-btn"
          >
            Ďalej →
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-b from-orange-500 to-orange-600 text-white font-bold text-sm shadow-md hover:from-orange-600 hover:to-orange-700 disabled:opacity-60"
            data-testid="sample-submit-btn"
          >
            {submitting ? 'Vyhodnocujem…' : 'Odoslať a vidieť výsledok'}
          </button>
        )}
      </div>

      <p className="mt-4 text-center text-xs text-slate-500">
        Toto je bezplatná ukážka. Tvoje odpovede sa neukladajú ani neevidujú.
      </p>
    </div>
  )
}
